import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Mail, 
  Phone, 
  Building2, 
  User, 
  AlertTriangle, 
  Copy,
  RefreshCw,
  CheckCircle2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001") + "/api";

const DuplicateLeadsDialog = ({ open, onClose, leadId, leadName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (open && leadId) {
      findDuplicates();
    }
  }, [open, leadId]);

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads/${leadId}/duplicates`, {
        withCredentials: true
      });
      setDuplicates(response.data.duplicates || []);
      if (response.data.duplicates_found === 0) {
        toast.success("No duplicates found!");
      }
    } catch (error) {
      toast.error("Failed to find duplicates");
      console.error(error);
    }
    setLoading(false);
  };

  const handleSelectDuplicate = (duplicateLeadId) => {
    const newSelected = new Set(selectedDuplicates);
    if (newSelected.has(duplicateLeadId)) {
      newSelected.delete(duplicateLeadId);
    } else {
      newSelected.add(duplicateLeadId);
    }
    setSelectedDuplicates(newSelected);
  };

  const handleMerge = async () => {
    if (selectedDuplicates.size === 0) {
      toast.error("Please select at least one duplicate to merge");
      return;
    }

    if (!window.confirm(`Are you sure you want to merge ${selectedDuplicates.size} duplicate lead(s) into this lead? This action cannot be undone.`)) {
      return;
    }

    setMerging(true);
    try {
      await axios.post(
        `${API}/leads/merge`,
        {
          primary_lead_id: leadId,
          duplicate_lead_ids: Array.from(selectedDuplicates)
        },
        { withCredentials: true }
      );
      
      toast.success(`Successfully merged ${selectedDuplicates.size} duplicate(s)`);
      setSelectedDuplicates(new Set());
      
      // Refresh the current page and close dialog
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge leads");
      console.error(error);
    }
    setMerging(false);
  };

  const getMatchTypeBadge = (matchType) => {
    const badges = {
      email: { label: "Email Match", color: "bg-blue-100 text-blue-700 border-blue-300" },
      phone: { label: "Phone Match", color: "bg-green-100 text-green-700 border-green-300" },
      name_company: { label: "Name+Company Match", color: "bg-purple-100 text-purple-700 border-purple-300" }
    };
    
    const badge = badges[matchType] || { label: matchType, color: "bg-slate-100 text-slate-700 border-slate-300" };
    
    return (
      <Badge variant="outline" className={badge.color}>
        {badge.label}
      </Badge>
    );
  };

  const getConfidenceBadge = (confidence) => {
    let color = "bg-slate-100 text-slate-700";
    if (confidence >= 0.9) color = "bg-green-100 text-green-700";
    else if (confidence >= 0.7) color = "bg-yellow-100 text-yellow-700";
    else color = "bg-red-100 text-red-700";

    return (
      <Badge className={color}>
        {Math.round(confidence * 100)}% confidence
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-indigo-600" />
            Find Duplicate Leads for "{leadName}"
          </DialogTitle>
          <DialogDescription>
            Found {duplicates.length} potential duplicate(s). Select duplicates to merge into this lead.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : duplicates.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700">No duplicates found!</p>
            <p className="text-sm text-slate-500 mt-2">This lead has no potential duplicates based on email, phone, or name+company.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {duplicates.map((duplicate) => {
              const duplicateLead = duplicate.lead;
              const isSelected = selectedDuplicates.has(duplicateLead.id);
              
              return (
                <Card 
                  key={duplicateLead.id} 
                  className={`border-2 transition-all cursor-pointer ${
                    isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => handleSelectDuplicate(duplicateLead.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleSelectDuplicate(duplicateLead.id)}
                        className="mt-1"
                      />
                      
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">
                              {duplicateLead.raw_data?.name || "Unknown"}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              {getMatchTypeBadge(duplicate.match_type)}
                              {getConfidenceBadge(duplicate.confidence)}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${duplicateLead.id}`);
                            }}
                          >
                            View Lead
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {duplicateLead.raw_data?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{duplicateLead.raw_data.email}</span>
                            </div>
                          )}
                          {duplicateLead.raw_data?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{duplicateLead.raw_data.phone}</span>
                            </div>
                          )}
                          {duplicateLead.raw_data?.company && (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{duplicateLead.raw_data.company}</span>
                            </div>
                          )}
                          {duplicateLead.raw_data?.title && (
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{duplicateLead.raw_data.title}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            Stage: {duplicateLead.stage}
                          </Badge>
                          <Badge variant="outline">
                            Status: {duplicateLead.enrichment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          {duplicates.length > 0 && (
            <Button 
              onClick={handleMerge} 
              disabled={selectedDuplicates.size === 0 || merging}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {merging ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Merge Selected ({selectedDuplicates.size})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateLeadsDialog;
